import React from "react";
import PropTypes from "prop-types";

import useSheetStack from "../common/useSheetStack";
import SheetStack from "../common/SheetStack";
import { MED_V2_ADD, MED_V2_STATUS, MED_V2_ADHERENCE } from "./events";
import MedicineForm from "./MedicineForm";
import MedicineStatusForm from "./MedicineStatusForm";
import MedicineAdherenceForm from "./MedicineAdherenceForm";

/**
 * Medicines editor rail — a stack of write sheets, newest on top.
 *
 * Board 2964:4270 draws the write surface as a card in the third column, so
 * the floating AddMedicineModal stack became rail sheets on the shared
 * useSheetStack, and the centred UpdateStatusModal followed as
 * MedicineStatusForm. The rail is ON-DEMAND (registry `editorOnDemand`):
 * the table keeps the column's width until a sheet opens. Trend stays a
 * modal — it is a read, not a write.
 */

const ADD = [MED_V2_ADD];
// Both "status" (Update medicine) and "adherence" (Review medicine) ride
// the edit channel because they open a sheet for a specific medicine.
// The payload's `mode` marker is stamped onto the record so the renderer
// can pick the right sheet component.
const EDIT = [MED_V2_STATUS, MED_V2_ADHERENCE];

const getRecord = (payload) => {
  if (!payload || !payload.medicine) return null;
  if (payload.mode === "adherence") {
    return { ...payload.medicine, __adherenceOnly: true };
  }
  if (payload.mode === "status") {
    return { ...payload.medicine, __statusOnly: true };
  }
  return payload.medicine;
};
// Dedup key. Update sheets: one per medicine (Rule 2 in useSheetStack).
// Adherence sheets: DO NOT dedup — every click opens a fresh blank review
// per the design lock ("green icon → clicking again opens a new dialog,
// this will be another entry"). Returning null bypasses the dedup check.
const getRecordId = (r) => {
  if (!r) return null;
  if (r.__adherenceOnly) return null;
  return r.medicineId;
};

const MedicinesEditor = ({ patient }) => {
  const patientId = patient && (patient.id || patient.patientID);

  const stack = useSheetStack({
    addEvents: ADD,
    editEvents: EDIT,
    getRecord,
    getRecordId,
    blankMessage: "A blank medicine form is already open.",
    dupMessage: (r) => `${(r && r.medicine) || "This medicine"} is already open for update.`,
    enabled: !!patientId,
  });

  if (!patientId) {
    return <div className="ph-slot-empty is-editor"><p>Waiting for patient…</p></div>;
  }

  return (
    <SheetStack
      stack={stack}
      className="med3-editor"
      describe={(s) => {
        if (s.editing && s.editing.__adherenceOnly) {
          return `Review · ${s.editing.medicine || "medicine"}`;
        }
        if (s.editing) {
          return `Update · ${s.editing.medicine || "medicine"}`;
        }
        return "Add medicine";
      }}
    >
      {(sheet, props) => {
        if (sheet.editing && sheet.editing.__adherenceOnly) {
          return <MedicineAdherenceForm patientId={patientId} {...props} />;
        }
        if (sheet.editing && sheet.editing.__statusOnly) {
          return <MedicineStatusForm patientId={patientId} {...props} />;
        }
        return <MedicineForm patientId={patientId} {...props} />;
      }}
    </SheetStack>
  );
};

MedicinesEditor.propTypes = {
  patient: PropTypes.object,
};

export default MedicinesEditor;
